/**
 * Page chrome for hub pages.
 *
 * - `forestLayout`: the start page look (neal.fun-style centred crown and
 *   wordmark, ferns growing in from the bottom corners).
 * - `workLayout`: compact header for the teacher area and editors.
 *
 * Board and student pages are full screen and use neither.
 */
import { api } from '@shared/api';
import { esc } from '@shared/dom';
import { t } from '@shared/i18n/de';
import crown from './art/crown.svg?raw';
import fern from './art/fern-corner.svg?raw';
import './hub.css';

export interface LayoutOptions {
  teacherName?: string | null;
}

function nav(options: LayoutOptions): string {
  return `<nav class="hub-nav" aria-label="Hauptnavigation">
    <a class="hub-nav__link" href="/anleitungen" data-link>${t('nav.guide')}</a>
    <a class="hub-nav__link" href="/spielen" data-link>${t('nav.play')}</a>
    <a class="hub-nav__link" href="/lehrer" data-link ${options.teacherName === undefined ? 'data-nav-teacher' : ''}>${options.teacherName != null ? esc(options.teacherName) : t('nav.teacher')}</a>
  </nav>`;
}

/**
 * Pages that do not know who is logged in (start, format pages, guides)
 * show the teacher's name too once it is known, as the teacher pages do.
 */
function fillTeacher(mount: HTMLElement): void {
  const link = mount.querySelector<HTMLElement>('[data-nav-teacher]');
  if (link === null) return;
  void api<{ teacher: { displayName: string } | null }>('/api/me/optional')
    .then((r) => {
      if (r.teacher !== null && link.isConnected) link.textContent = r.teacher.displayName;
    })
    .catch(() => undefined);
}

/** Start and format pages. Returns the main element to fill. */
export function forestLayout(
  mount: HTMLElement,
  body: string,
  options: LayoutOptions = {},
): HTMLElement {
  mount.innerHTML = `
    <div class="forest">
      <div class="forest__top">${nav(options)}</div>
      <header class="forest__header">
        <a class="forest__brand" href="/" data-link aria-label="${t('app.name')}, zur Startseite">
          <span class="forest__crown" aria-hidden="true">${crown}</span>
          <span class="forest__title">${t('app.name')}</span>
        </a>
        <p class="forest__tagline">${t('app.tagline')}</p>
      </header>
      <main class="page forest__main"></main>
      <div class="forest__fern forest__fern--left" aria-hidden="true">${fern}</div>
      <div class="forest__fern forest__fern--right" aria-hidden="true">${fern}</div>
    </div>`;
  const main = mount.querySelector<HTMLElement>('.forest__main');
  if (main === null) throw new Error('layout failed');
  main.innerHTML = body;
  fillTeacher(mount);
  return main;
}

/** Teacher area, editors, session pages. Returns the main element to fill. */
export function workLayout(
  mount: HTMLElement,
  body: string,
  options: LayoutOptions = {},
): HTMLElement {
  mount.innerHTML = `
    <header class="work-header">
      <a class="work-header__brand" href="/" data-link>
        <span class="work-header__crown" aria-hidden="true">${crown}</span>
        <span class="work-header__title">${t('app.name')}</span>
      </a>
      ${nav(options)}
    </header>
    <main class="page work-main"></main>`;
  const main = mount.querySelector<HTMLElement>('.work-main');
  if (main === null) throw new Error('layout failed');
  main.innerHTML = body;
  fillTeacher(mount);
  return main;
}

/** A fixed strip for connection trouble. Empty when the connection is fine. */
export function connectionBanner(status: string): string {
  if (status === 'open') return '';
  const key =
    status === 'reconnecting'
      ? 'conn.reconnecting'
      : status === 'closed'
        ? 'conn.closed'
        : 'conn.connecting';
  return `<div class="conn-banner conn-banner--${esc(status)}" role="status">${t(key)}</div>`;
}
